import React, { useEffect, useState } from "react";
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import { ReactComponent as DropdownIcon } from "../../../../../assets/icons/paraplanningDashboard/ArrowDropdown.svg";
import { db } from "../../../../../backend/firebaseConfig";
import { PostCard } from "../../../Tabs/HelperFunctions/HelperFunctions";
import { CPD_REPORT_UPLOADS_COLLECTION } from "../utils/cpdRecords";
import "../../../../../css/Adviser/LicenseCertificatesSummary.css";

const EMPTY_VALUE = "-";

const formatDateTime = (value) => {
  if (!value) return EMPTY_VALUE;

  const date = value?.toDate ? value.toDate() : new Date(value);

  if (Number.isNaN(date.getTime())) return EMPTY_VALUE;

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const DATE_FIELD_KEYWORDS = [
  "date",
  "expiry",
  "meeting",
  "signoff",
  "signOff",
  "oca",
  "cas",
  "rriar",
  "vct",
  "test",
  "pdp",
];

const formatDateOnly = (value) => {
  if (!value) return EMPTY_VALUE;

  const stringValue = String(value).trim();
  let date;

  if (value?.toDate) {
    date = value.toDate();
  } else if (/^\d{4}-\d{2}-\d{2}$/.test(stringValue)) {
    const [year, month, day] = stringValue.split("-").map(Number);
    date = new Date(year, month - 1, day);
  } else if (/^\d{2}\/\d{2}\/\d{4}$/.test(stringValue)) {
    const [day, month, year] = stringValue.split("/").map(Number);
    date = new Date(year, month - 1, day);
  } else {
    date = new Date(value);
  }

  if (Number.isNaN(date.getTime())) return stringValue || EMPTY_VALUE;

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const isDateField = (fieldPath = "") => {
  const normalisedPath = String(fieldPath).toLowerCase();

  return DATE_FIELD_KEYWORDS.some((keyword) =>
    normalisedPath.includes(keyword.toLowerCase()),
  );
};

const formatLogValue = (value, fieldPath = "") => {
  if (value === null || value === undefined || value === "") {
    return EMPTY_VALUE;
  }

  if (isDateField(fieldPath)) {
    return formatDateOnly(value);
  }

  return String(value);
};

const formatFileSize = (value) => {
  const bytes = Number(value || 0);
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatFieldLabel = (fieldPath = "") => {
  return fieldPath
    .split(".")
    .map((part) =>
      part
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (char) => char.toUpperCase()),
    )
    .join(" / ");
};

const LicenseCertificateEditLogs = ({
  adviserId,
  variant = "adviser",
}) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const isCpdReports = variant === "cpdReports";

  useEffect(() => {
    if (!open) return undefined;

    if (!isCpdReports && !adviserId) {
      setLogs([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);

    const logsRef = isCpdReports
      ? collection(db, CPD_REPORT_UPLOADS_COLLECTION)
      : collection(
          db,
          "Authorized Members",
          "Advisers",
          "Data",
          adviserId,
          "License Certificates",
          "Data",
          "Edit Logs",
        );

    const logsQuery = query(
      logsRef,
      orderBy(isCpdReports ? "importedAt" : "updatedAt", "desc"),
      limit(50),
    );

    return onSnapshot(
      logsQuery,
      (snapshot) => {
        setLogs(
          snapshot.docs.map((docSnap) => ({
            id: docSnap.ref.path,
            ...docSnap.data(),
          })),
        );
        setLoading(false);
      },
      (error) => {
        console.error("Failed to load licence certificate edit logs:", error);
        setLogs([]);
        setLoading(false);
      },
    );
  }, [adviserId, isCpdReports, open]);

  return (
    <div className="smFeed">
      <PostCard
        title={isCpdReports ? "CPD Report Edit Logs" : "Edit Logs"}
        right={
          <button
            type="button"
            className="table-row-action"
            onClick={() => setOpen((current) => !current)}
            aria-label={open ? "Collapse edit logs" : "Expand edit logs"}
            aria-expanded={open}
            title={open ? "Collapse edit logs" : "Expand edit logs"}
          >
            <DropdownIcon
              aria-hidden
              style={{
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease",
              }}
            />
          </button>
        }
      >
        {open ? (
          <>
          {loading ? (
            <p className="Content-Small muted">Loading edit logs...</p>
          ) : null}

          {!loading && logs.length === 0 ? (
            <p className="Content-Small muted">
              No edit logs found yet. New changes will appear here after saving.
            </p>
          ) : null}

          {!loading && logs.length > 0 ? (
            <div className="lcEditLogsList">
              {logs.map((log) => (
                <div key={log.id} className="lcEditLogItem">
                  <div className="lcEditLogHeader">
                    <span className="FP_Help_Text_Semibold">
                      {isCpdReports
                        ? log.importedByName ||
                          log.importedBy ||
                          "Unknown user"
                        : log.updatedByName ||
                          log.updatedBy ||
                          "Unknown user"}
                    </span>

                    <span className="FP_Help_Text muted">
                      {formatDateTime(
                        isCpdReports ? log.importedAt : log.updatedAt,
                      )}
                    </span>
                  </div>

                  <div className="lcEditLogChanges">
                    {isCpdReports ? (
                      <div className="lcEditLogChange">
                        <div className="FP_Help_Text_Semibold">
                          CPD report upload
                        </div>
                        <div className="FP_Help_Text">
                          <span className="muted">Matched records:</span>{" "}
                          {Number(log.matchedCount || 0)}
                        </div>
                        <div className="FP_Help_Text">
                          <span className="muted">Unmatched records:</span>{" "}
                          {Number(log.unmatchedCount || 0)}
                        </div>
                        <div className="lcCpdUploadFiles">
                          {(log.files || []).map((file) => (
                            <a
                              key={`${log.id}-${file.reportType}-${file.name}`}
                              className="lcCpdUploadFileLink FP_Help_Text_Semibold"
                              href={file.url}
                              download={file.name}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <span>
                                {file.reportType === "idd" ? "IDD" : "All CPD"}: {file.name}
                              </span>
                              {formatFileSize(file.size) ? (
                                <span className="FP_Help_Text muted">
                                  {formatFileSize(file.size)} · Download
                                </span>
                              ) : null}
                            </a>
                          ))}
                        </div>
                      </div>
                    ) : (
                      (log.changes || []).map((change, index) => (
                        <div
                          key={`${log.id}-${change.fieldPath}-${index}`}
                          className="lcEditLogChange"
                        >
                          <div className="FP_Help_Text_Semibold">
                            {formatFieldLabel(change.fieldPath)}
                          </div>

                          <div className="FP_Help_Text">
                            <span className="muted">From:</span>{" "}
                            {formatLogValue(
                              change.previousValue,
                              change.fieldPath,
                            )}
                          </div>

                          <div className="FP_Help_Text">
                            <span className="muted">To:</span>{" "}
                            {formatLogValue(change.newValue, change.fieldPath)}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
          </>
        ) : null}
      </PostCard>
    </div>
  );
};

export default LicenseCertificateEditLogs;
